var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext } from "react";
import { Box } from "@mui/system";
import { Resource, useSidebarState } from "react-admin";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/providers/AuthContextProvider";
import { Tabs } from "@/components/Tabs";
import { HeaderTitle } from "@/components/HeaderTitle";
import { SettingsLanguages } from "@/resources/Settings/SettingsLanguages";
import { SettingsMessaging } from "@/resources/Settings/SettingsMessaging";
import { SettingsVotingChannels } from "@/resources/Settings/SettingsVotingChannel";
import { SettingsElectionsTypes } from "@/resources/Settings/SettingsElectionsTypes";
import { SettingsElectionsTypesCreate } from "@/resources/Settings/SettingsElectionsTypesCreate";
import { IPermissions } from "@/types/keycloak";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { ResourceListStyles } from "@/components/styles/ResourceListStyles";
import { Typography } from "@mui/material";
import { SettingsTrustees } from "@/resources/Settings/SettingsTrustees";
import { SettingsLookAndFeel } from "@/resources/Settings/SettingsLookAndFeel";
import { SettingsIntegrations } from "@/resources/Settings/SettingsIntegrations";
import { SettingsCountries } from "@/resources/Settings/SettingsCountries";
import SettingsLocalization from "@/resources/Settings/SettingsLocalization";
import { SettingsBackupRestore } from "@/resources/Settings/SettingsBackupRestore";
import { SettingsConfigurationPackage } from "@/resources/Settings/SettingsConfigurationPackage";
import { mayApplyPackages } from "@/resources/Settings/configurationPackage";
import { SettingsPreviews } from "@/resources/Settings/SettingsPreviews";
import { SettingsLogs } from "@/resources/Settings/SettingsLogs";
import { PluginSettings } from "@/plugins/PluginSettings";
import { ESettingsAccess, settingsAccess, showsConfiguration, showsLogs, } from "@/services/settingsAccess";
export var SettingsScreen = function () {
    var t = useTranslation().t;
    var authContext = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var access = settingsAccess(function (permission) {
        return authContext.isAuthorized(true, tenantId, permission);
    });
    var open = useSidebarState()[0];
    // Only with every permission the package's sections need, on this tenant.
    var showConfigurationPackage = mayApplyPackages(tenantId, authContext.isAuthorized);
    var showMessaging = authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_ACCOUNT_READ);
    if (access === ESettingsAccess.NONE) {
        return (_jsx(ResourceListStyles.EmptyBox, { children: _jsx(Typography, { variant: "h4", paragraph: true, children: t("electionTypeScreen.noPermissions") }) }));
    }
    var configurationTabs = __spreadArray(__spreadArray([
        {
            label: t("electionTypeScreen.tabs.electionTypes"),
            component: function () { return (_jsx(Resource, { name: "sequent_backend_election_type", list: SettingsElectionsTypes, create: SettingsElectionsTypesCreate, edit: SettingsElectionsTypesCreate, show: SettingsElectionsTypesCreate })); },
        },
        {
            label: t("electionTypeScreen.tabs.votingChannels"),
            component: function () { return (_jsx(Resource, { name: "sequent_backend_tenant", list: SettingsVotingChannels })); },
        }
    ], (showMessaging
        ? [
            {
                label: t("messagingAccounts.tab"),
                component: function () { return _jsx(SettingsMessaging, {}); },
            },
        ]
        : []), true), [
        {
            label: t("electionTypeScreen.tabs.languages"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_tenant", list: SettingsLanguages }); },
        },
        {
            label: t("electionTypeScreen.tabs.localization"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_tenant", list: SettingsLocalization }); },
        },
        {
            label: t("electionTypeScreen.tabs.integrations"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_tenant", list: SettingsIntegrations }); },
        },
        {
            label: t("electionTypeScreen.tabs.lookAndFeel"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_tenant", list: SettingsLookAndFeel }); },
        },
        {
            label: t("electionTypeScreen.tabs.trustees"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_trustee", list: SettingsTrustees }); },
        },
        {
            label: "Countries",
            component: function () { return _jsx(Resource, { name: "sequent_backend_tenant", list: SettingsCountries }); },
        },
        {
            label: t("electionTypeScreen.tabs.BackupRestore"),
            component: function () { return (_jsx(Resource, { name: "sequent_backend_tenant", list: SettingsBackupRestore })); },
        },
        {
            label: t("settings.previewScreen.label"),
            component: function () { return _jsx(Resource, { name: "sequent_backend_preview", list: SettingsPreviews }); },
        },
        { label: t("plugins.title"), component: function () { return _jsx(PluginSettings, {}); } },
    ], false);
    return (_jsxs(Box, { sx: { maxWidth: "calc(100vw - ".concat(open ? "352px" : "96px", ")"), bgcolor: "background.paper" }, className: "settings-box", children: [_jsx(HeaderTitle, { title: String(t("electionTypeScreen.common.settingTitle")), subtitle: String(t("electionTypeScreen.common.settingSubtitle")) }), _jsx(Tabs, { elements: __spreadArray(__spreadArray(__spreadArray([], (showsConfiguration(access) ? configurationTabs : []), true), (showsLogs(access)
                    ? [
                        {
                            label: t("electionTypeScreen.tabs.logs"),
                            component: function () { return _jsx(SettingsLogs, {}); },
                        },
                    ]
                    : []), true), (showsConfiguration(access) && showConfigurationPackage
                    ? [
                        {
                            label: t("configurationPackage.tab"),
                            component: function () { return _jsx(SettingsConfigurationPackage, {}); },
                        },
                    ]
                    : []), true) })] }));
};
